import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Consume a password-reset token and set a new password. On success, the
// backend auto-logs the user in (returns access_token + sets cookies). We
// pipe the response through applyAuthSession so the canonical TOKEN_KEY is
// written and the axios interceptor's Bearer header just works.
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyAuthSession } = useAuth();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      toast.error('Missing reset token.');
      navigate('/member-login', { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password });
      applyAuthSession(res.data);
      setDone(true);
      toast.success('Password updated — you are now signed in.');
      setTimeout(() => navigate('/portal', { replace: true }), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset password. Your link may have expired.';
      setErrorMsg(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#002249] flex items-center justify-center px-4"
      data-testid="reset-password-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#002249] to-[#1D3557] px-8 py-7 text-center">
            <ShieldCheck className="w-11 h-11 text-white mx-auto mb-2.5" />
            <h1 className="text-xl font-bold text-white">Choose a new password</h1>
            <p className="text-white/60 text-xs mt-1">Dominican Chamber of Commerce of Florida</p>
          </div>

          {done ? (
            <div className="p-8 text-center" data-testid="reset-password-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#002249] mb-2">Password updated</h2>
              <p className="text-slate-600 text-sm">
                You are now signed in. Taking you to your portal...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-5" data-testid="reset-password-form">
              <p className="text-sm text-slate-600 text-center">
                Create a new password to regain access to your DCCF account.
              </p>

              {errorMsg && (
                <div
                  className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3"
                  data-testid="reset-password-error"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">{errorMsg}</p>
                    <Link to="/forgot-password" className="underline mt-1 inline-block">
                      Request a new reset link →
                    </Link>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="rp-new" className="text-xs font-semibold text-slate-700">New password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="rp-new"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter at least 6 characters"
                    className="pl-10 pr-10 h-11 rounded-lg text-[16px]"
                    data-testid="reset-password-new"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="rp-confirm" className="text-xs font-semibold text-slate-700">Confirm password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="rp-confirm"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your new password"
                    className="pl-10 h-11 rounded-lg text-[16px]"
                    data-testid="reset-password-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-lg font-semibold text-sm"
                data-testid="reset-password-submit"
              >
                {loading ? 'Updating...' : 'Update password & sign in'}
              </Button>

              <p className="text-[11px] text-slate-400 text-center">
                This link expires 60 minutes after it was requested and can only be used once.
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
